import MomentUtils from '@date-io/moment';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import FormTextField from '../../components/FormTextField';
import StoryItem from '../../components/StoryItem';
import { SubmitControl } from '../../components/SubmitControl';
import { useTransferContract, useTransferContractMutation } from '../../hooks/queries/useTransferContract';
import useAlertDialog from '../../hooks/useAlertDialog';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useSnackbar from '../../hooks/useSnackbar';
import useStorySelect from '../../hooks/useStorySelect';
import settings from '../../services/settings';
import {
  IStory,
  ITransferContract,
  TransferContractDealType,
  ISuggestionSource,
  ISuggestionSourceUser,
  IUser,
  IMinimumGuarantee,
} from '../../services/types';
import { getFormatedDealTypeAndAmount } from './ContractList';
import AutoComplete from '../../components/AutoComplete';
import SearchTextField, { ISearchTextField } from '../../components/SearchTextField';
import ForwardIcon from '@material-ui/icons/Forward';
import { isPositiveNumber, numberToCurrency, numberToPercentage } from '../../services/utils';
import MinimumGuaranteeValidator from './validator';

interface IProps {
  contractId?: number;
  onCreate?: (contract: ITransferContract) => void;
  onRemove?: (contract: ITransferContract) => void;
}

type ITargetTypes = 'story' | 'season' | 'writer';

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    marginTop: 16,
    padding: 20,
  },

  targetTypeDescription: {
    fontSize: 12,
    color: '#777777',
    paddingBottom: 10,
  },

  storyItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 347,
    minHeight: 68,
    marginTop: 8,
    border: '1px solid #dcdcdc',
  },

  endDateTextField: {
    width: 60,
    height: 34,
    marginRight: 12,
  },

  text: {
    fontSize: 14,
    color: '#888',
    marginTop: 8,
  },

  value: {
    height: 19,
    backgroundColor: '#f1f1f1',
    borderRadius: 2,
    marginTop: 6,
    marginLeft: 8,
    padding: '2px 4px 1px',
    fontSize: 14,
    fontWeight: 'bold',
    color: '#4f4f4f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  chip: {
    width: 'fit-content',
    color: '#a2a2a2',
    backgroundColor: theme.palette.type === 'dark' ? '#f2f2f218' : '#f2f2f2',
    marginRight: 8,
    marginBottom: 4,
  },

  minimumGuaranteeArea: {
    display: 'flex',
    margin: '20px',
  },

  minimumGuaranteeBox: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    borderRadius: '6px',
    padding: '10px',
  },

  minimumGuaranteeFormField: {
    marginLeft: '12px',
    width: '107px',
    backgroundColor: 'white',
    marginTop: '10px',
    marginBottom: '15px',
  },

  minimumGuaranteeArrow: {
    alignSelf: 'center',
    backgroundColor: 'white',
    borderRadius: '6px',
    padding: '10px',
  },

  minimumGuaranteeValue: {
    height: '20px',
    marginTop: '8px',
    marginBottom: '15px',
    fontSize: 15,
    fontWeight: 'bold',
    color: '#212121',
  },
}));

const initialState = {
  dealType: undefined,
  value: 0,
  startsAt: moment().format(moment.HTML5_FMT.DATE),
  endsAt: moment().add(1, 'day').format(moment.HTML5_FMT.DATE),
  minimumGuarantee: {
    advancePayment: 0,
    shareDuring: 0,
    shareAfter: 0,
  },
};

const ContractView = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const alert = useAlertDialog();
  const confirm = useConfirmDialog();

  const { contractId: currentContractId, onCreate, onRemove } = props;

  const [contract, setContract] = useState<Partial<ITransferContract>>(initialState);

  const [mode, setMode] = useState<'create' | 'edit' | 'view'>(currentContractId ? 'view' : 'create');

  const { data: contractFromQuery, isLoading } = useTransferContract(currentContractId);
  const { create, update, remove } = useTransferContractMutation();

  const [targetType, setTargetType] = useState<ITargetTypes>('season');
  const [targetStory, setTargetStory] = useState<IStory>();
  const [endDateSettingMode, setEndDateSettingMode] = useState<'custom' | 'calendar'>('custom');

  const [validationMessages, setValidationMessages] = useState<Set<string>>(new Set<string>());

  const storySelect = useStorySelect();
  const scopeRef = useRef<HTMLDivElement>();

  const [formattedDealType, formattedDealAmount] = getFormatedDealTypeAndAmount(contract?.dealType, contract.value);

  const handleSelectTargetClick = () => {
    if (targetType === 'writer') {
      return;
    }
    storySelect.show({
      title: targetType === 'season' ? 'Select a Season' : 'Select a Story',
      filter: targetType,
      onSelect: (story: IStory) => {
        const { seasonId, seasons } = story;

        if (targetType === 'season' && !(seasonId || seasons)) {
          snackbar.error('Error');
          return;
        }

        setContract(ps => {
          if (targetType === 'season') {
            return { ...ps, seasonId, story: undefined, storyId: null };
          }

          return { ...ps, storyId: story.id, season: undefined, seasonId: null };
        });

        setTargetStory(story);
      },
      scopeRef,
    });
  };

  const handleStartsAtChange = (date: MaterialUiPickersDate) => {
    if (date) {
      setContract(ps => ({ ...ps, startsAt: date.format(moment.HTML5_FMT.DATE) }));

      if (date.isAfter(contract.endsAt)) {
        setContract(ps => ({ ...ps, endsAt: date.add(1, 'day').format(moment.HTML5_FMT.DATE) }));
      }
    }
  };

  const handleEndsAtChange = (date: MaterialUiPickersDate) => {
    if (date) {
      setContract(ps => ({ ...ps, endsAt: date.format(moment.HTML5_FMT.DATE) }));
    }
  };

  const handleCustomEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const daysLater = Number(e.target.value);

    const endDate = moment(contract.startsAt)
      .add(daysLater || 0, 'days')
      .format(moment.HTML5_FMT.DATE);

    setContract(ps => ({ ...ps, endsAt: endDate }));
  };

  const handleEditClick = () => setMode('edit');

  const handleDealTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value) {
      setContract(ps => ({
        ...ps,
        dealType: value as TransferContractDealType,
        value: 0,
        minimumGuarantee: {
          advancePayment: 0,
          shareDuring: 0,
          shareAfter: 0,
        },
      }));
    }
  };

  const handleValueChange = (value: string) => {
    setContract(ps => ({ ...ps, value: /^\d+\.?\d*$/.test(value) ? Number(value) : value }));
  };

  const handleMinimumGuaranteeChange = (property: keyof IMinimumGuarantee, value: string) => {
    setContract(ps => ({
      ...ps,
      minimumGuarantee: {
        ...ps.minimumGuarantee,
        [property]: /^\d+\.?\d*$/.test(value) ? Number(value) : value,
      },
    }));
  };

  const handleSubmitClick = async () => {
    try {
      const result = mode === 'create' ? await create.mutateAsync(contract) : await update.mutateAsync(contract);

      if (mode === 'edit') {
        setContract(result);
        setMode('view');
      } else {
        reset();
      }

      if (onCreate) {
        onCreate(result);
      }
    } catch (e) {
      if (e.message === 'OTHER_CONTRACTS_IN_THIS_PERIOD') {
        alert.open({
          title: 'Alert',
          text: 'There is an existing contract for the season in the selected contract period. Please check the existing contract and try again. ',
        });
      } else if (e.message === 'ALREADY_TRANSFERRED_IN_PERIOD') {
        alert.open({
          title: 'Alert',
          text: 'The date you have selected includes a period that has already been paid for. Please select a different date. ',
        });
      }
    }
  };

  const handleCancelClick = () => {
    const originalData = mode === 'create' ? initialState : contractFromQuery;

    if (originalData === contract) {
      if (mode !== 'create') {
        setMode('view');
      }
      return;
    }

    confirm.open({
      text: 'Discard any changes?',
      onProceed: reset,
    });
  };

  const reset = () => {
    const originalData = mode === 'create' ? initialState : contractFromQuery;

    if (!originalData) {
      return;
    }

    setContract(originalData);
    setEndDateSettingMode('custom');

    if (mode === 'create') {
      setTargetType('season');
      setTargetStory(undefined);
    } else {
      setMode('view');
    }
  };

  const handleStoryItemClick = () => {
    const season = contract.season || contract.story?.firstSeason;

    if (!season) {
      return;
    }

    window.open(`${settings.getCrewURI()}/stories/${season.storyId}/seasons/${season.id}`, '_blank', 'noopener noreferrer');
  };

  const handleDeleteClick = () => {
    const onProceed = async () => {
      try {
        const result = await remove.mutateAsync(contract);

        if (onRemove) {
          onRemove(result.data);
        }
      } catch (e) {
        if (e.message === 'ALREADY_TRANSFERRED_IN_PERIOD') {
          alert.open({
            title: 'Alert',
            text: 'This contract cannot be deleted because payment for some period has already been transferred.',
          });
        }
      }
    };

    confirm.open({
      title: 'Delete this contract?',
      text: 'Once you delete, you cannot restore the contract.',
      cancelButtonText: 'Keep',
      proceedButtonText: 'Delete',
      onProceed,
    });
  };

  useEffect(() => {
    if (contractFromQuery) {
      setContract(contractFromQuery);

      if (contractFromQuery.storyId) {
        setTargetType('story');
        setTargetStory(contractFromQuery.story);
      } else if (contractFromQuery.seasonId) {
        setTargetType('season');

        const story = Object.assign({}, contractFromQuery.season!.story, {
          seasonId: contractFromQuery.seasonId,
          seasons: [contractFromQuery.season],
        });

        setTargetStory(story);
      } else if (contractFromQuery.writerId) {
        setTargetType('writer');
      }
    }
  }, [contractFromQuery]);

  useEffect(() => {
    const messageSet = new Set<string>();

    const { value, dealType, writerId, minimumGuarantee } = contract;

    if (targetType !== 'writer' && !targetStory) {
      messageSet.add('Please select a story or a season.');
    }

    if (targetType === 'writer' && !writerId) {
      messageSet.add('Please select a writer.');
    }

    if (!isPositiveNumber(value)) {
      messageSet.add('Please enter a valid value in the Deal Type field.');
    }

    if (
      dealType === TransferContractDealType.MinimumGuarantee &&
      (!isPositiveNumber(minimumGuarantee!.advancePayment) ||
        !isPositiveNumber(minimumGuarantee!.shareDuring) ||
        !isPositiveNumber(minimumGuarantee!.shareAfter))
    ) {
      messageSet.add('Please enter a valid value in the Minimum Guarantee field.');
    }

    if (!dealType) {
      messageSet.add('Please select a deal type.');
    }

    setValidationMessages(messageSet);
  }, [contract, targetStory, targetType]);

  return (
    // eslint-disable-next-line
    // @ts-ignore due to: https://github.com/mui-org/material-ui/issues/17010
    <Box ref={scopeRef}>
      <Typography variant="h6">
        {mode !== 'create' ? contractFromQuery?.story?.title || contractFromQuery?.season?.title : 'Create a Contract'}
      </Typography>
      <Paper className={classes.wrap}>
        {mode !== 'create' && contractFromQuery && !contractFromQuery.writerId && (
          <Box marginBottom="30px">
            <Typography>Original Story Title</Typography>
            <Box display="flex" alignItems="center">
              <Typography className={classes.text}>{contractFromQuery.originalStoryTitle}</Typography>
              <Typography variant="caption" style={{ marginLeft: '6px', marginTop: '8px' }}>
                {`(Created at: ${moment(contractFromQuery.createdAt).format('YYYY/MM/DD')})`}
              </Typography>
            </Box>
          </Box>
        )}
        <Box marginBottom="30px">
          {mode === 'create' && (
            <>
              <Typography>Target Type</Typography>
              <RadioGroup row value={targetType} onChange={e => setTargetType(e.target.value as 'season' | 'story' | 'writer')}>
                <FormControlLabel value={'season'} control={<Radio color="primary" />} label="Season" />
                <FormControlLabel value={'story'} control={<Radio color="primary" />} label="Story" />
                <FormControlLabel value={'writer'} control={<Radio color="primary" />} label="Writer's account(User ID)" />
              </RadioGroup>
              <Typography className={classes.targetTypeDescription}>
                {targetType === 'season'
                  ? '* When creating a season contract, the contract is applied only to the selected Season.'
                  : targetType === 'story'
                  ? '* When creating a story contract, all seasons of the story and the future seasons are included in the contract.'
                  : '* When creating a username contract, all stories and the future stories are included in the contract.'}
              </Typography>
              {targetType !== 'writer' && (
                <Button variant="outlined" color="primary" onClick={handleSelectTargetClick}>{`Select a ${targetType}`}</Button>
              )}
              {targetType === 'writer' && (
                <Box width={'360px'}>
                  <AutoComplete
                    type="writer"
                    render={(props: ISearchTextField) => {
                      const { value, ...restProps } = props;
                      return <SearchTextField defaultValue={value as string} placeholder="Search" fullWidth {...restProps} />;
                    }}
                    onSelect={(source: ISuggestionSource) => {
                      const { id, writer_name } = source as ISuggestionSourceUser;
                      const newWriter: Partial<IUser> = {
                        id: Number(id),
                        username: writer_name[0],
                      };
                      setContract(ps => ({ ...ps, writerId: newWriter.id, writer: newWriter }));
                    }}
                    resetFormOnSelect={true}
                  />
                  {contract.writer && (
                    <Box marginTop={'12px'}>
                      <Chip
                        key={contract.writer.id}
                        label={contract.writer.username}
                        onDelete={() => setContract(ps => ({ ...ps, writerId: null, writer: undefined }))}
                        onClick={() => window.open(`${settings.getCrewURI()}/users/${contract.writer!.id!}`, '_blank', 'noopener noreferrer')}
                        className={classes.chip}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}

          {mode !== 'create' && <Typography>Target</Typography>}

          {mode !== 'create' && targetType === 'writer' && (
            <Box>
              {contract.writer!.username} ({contract.writerId})
            </Box>
          )}
          {targetType !== 'writer' && (
            <Box>
              {targetStory && (
                <Box className={classes.storyItem}>
                  <StoryItem
                    story={targetStory}
                    readonly={true}
                    removable={mode === 'create'}
                    onRemoveClick={() => setTargetStory(undefined)}
                    onItemClick={handleStoryItemClick}
                  />
                </Box>
              )}
              {!targetStory && isLoading && (
                <Box className={classes.storyItem}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          )}
        </Box>

        {mode !== 'create' && (
          <Box marginBottom="30px">
            <Typography>Username(Payment link)</Typography>
            <Typography className={classes.text}>
              {contract.season?.paymentWriter.username || contract.story?.firstSeason.paymentWriter.username || contract.writer?.username}
              {isLoading && <Skeleton width="300px" />}
            </Typography>
          </Box>
        )}

        <Box marginBottom="30px">
          <Typography variant="subtitle1">(License) Effective Date</Typography>
          {/* common comp.. */}
          {mode !== 'view' && (
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/DD/YYYY"
                margin="dense"
                value={contract.startsAt}
                autoOk={true}
                onChange={handleStartsAtChange}
              />
            </MuiPickersUtilsProvider>
          )}

          {mode === 'view' && (
            <Typography className={classes.text}>
              {contractFromQuery && moment(contractFromQuery.startsAt).format('MM/DD/YYYY')}
              {isLoading && <Skeleton width="300px" />}
            </Typography>
          )}
        </Box>

        <Box marginBottom="30px">
          <Typography variant="subtitle1">(License) End Date</Typography>
          {mode !== 'view' && (
            <RadioGroup row value={endDateSettingMode} onChange={e => setEndDateSettingMode(e.target.value as 'custom' | 'calendar')}>
              <FormControlLabel value={'custom'} control={<Radio color="primary" />} label="Custom" />
              <FormControlLabel value={'calendar'} control={<Radio color="primary" />} label="Calendar" />
            </RadioGroup>
          )}

          {mode !== 'view' && endDateSettingMode === 'custom' && (
            <Box display="flex" alignItems="center">
              <TextField
                variant="outlined"
                size="small"
                value={contract.startsAt ? moment(contract.endsAt).diff(contract.startsAt, 'days') : undefined}
                onChange={handleCustomEndDateChange}
                className={classes.endDateTextField}
                style={{ width: 60 }}
              />
              <Typography>{`Days later (${moment(contract.endsAt).format('MM/DD/YYYY')})`}</Typography>
            </Box>
          )}

          {mode !== 'view' && endDateSettingMode === 'calendar' && (
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/DD/YYYY"
                margin="dense"
                value={contract.endsAt}
                autoOk={true}
                minDate={moment(contract.startsAt).add(1, 'day').startOf('day').toDate()}
                onChange={handleEndsAtChange}
              />
            </MuiPickersUtilsProvider>
          )}

          {mode === 'view' && (
            <Typography className={classes.text}>
              {contractFromQuery && moment(contractFromQuery.endsAt).format('MM/DD/YYYY')}
              {isLoading && <Skeleton width="300px" />}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography>Deal Type</Typography>
          {mode === 'create' && (
            <RadioGroup value={contract.dealType || null} onChange={handleDealTypeChange}>
              <FormControlLabel
                value={TransferContractDealType.FlatFee}
                control={<Radio color="primary" />}
                label={
                  <Box display="flex" alignItems="center">
                    <Typography>Flat Fee</Typography>
                    {contract.dealType === TransferContractDealType.FlatFee && (
                      <FormTextField
                        variant="outlined"
                        defaultValue={String(contract.value)}
                        onValueSubmit={handleValueChange}
                        size="small"
                        maxLength={8}
                        style={{ marginLeft: '12px', width: '146px' }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    )}
                  </Box>
                }
              />
              <FormControlLabel
                value={TransferContractDealType.RevenueShare}
                control={<Radio color="primary" />}
                label={
                  <Box display="flex" alignItems="center">
                    <Typography>Revenue Share</Typography>
                    {contract.dealType === TransferContractDealType.RevenueShare && (
                      <FormTextField
                        variant="outlined"
                        defaultValue={String(contract.value)}
                        onValueSubmit={handleValueChange}
                        size="small"
                        style={{ marginLeft: '12px', width: '67px' }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                      />
                    )}
                  </Box>
                }
              />
              <FormControlLabel
                value={TransferContractDealType.FixedCoinPrice}
                control={<Radio color="primary" />}
                label={
                  <Box display="flex" alignItems="center">
                    <Typography>Fixed Coin Price</Typography>
                    {contract.dealType === TransferContractDealType.FixedCoinPrice && (
                      <FormTextField
                        variant="outlined"
                        defaultValue={String(contract.value)}
                        onValueSubmit={handleValueChange}
                        size="small"
                        style={{ marginLeft: '12px', width: '107px' }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    )}
                  </Box>
                }
              />
              <FormControlLabel
                value={TransferContractDealType.MinimumGuarantee}
                control={<Radio color="primary" />}
                label={
                  <Box display="flex" alignItems="center">
                    <Typography>Minimum Guarantee</Typography>
                  </Box>
                }
              />
              {contract.dealType === TransferContractDealType.MinimumGuarantee && (
                <div className={classes.minimumGuaranteeArea}>
                  <span className={classes.minimumGuaranteeBox}>
                    Advance Payment(Required)
                    <FormTextField
                      variant="outlined"
                      defaultValue={String(contract.minimumGuarantee?.advancePayment)}
                      onValueSubmit={val => handleMinimumGuaranteeChange('advancePayment', val)}
                      size="small"
                      className={classes.minimumGuaranteeFormField}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      validators={[MinimumGuaranteeValidator.PositiveNumberOnly]}
                      required={true}
                    />
                    Rev. share % during Advance payment period(Required)
                    <FormTextField
                      variant="outlined"
                      defaultValue={String(contract.minimumGuarantee?.shareDuring)}
                      onValueSubmit={val => handleMinimumGuaranteeChange('shareDuring', val)}
                      size="small"
                      className={classes.minimumGuaranteeFormField}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      validators={[MinimumGuaranteeValidator.PositiveNumberOnly]}
                      required={true}
                    />
                  </span>
                  <span className={classes.minimumGuaranteeArrow}>
                    <ForwardIcon color="primary" fontSize="large" />
                  </span>
                  <span className={classes.minimumGuaranteeBox}>
                    Rev. share % after advance payment(Required)
                    <FormTextField
                      variant="outlined"
                      defaultValue={String(contract.minimumGuarantee?.shareAfter)}
                      onValueSubmit={val => handleMinimumGuaranteeChange('shareAfter', val)}
                      size="small"
                      className={classes.minimumGuaranteeFormField}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      validators={[MinimumGuaranteeValidator.PositiveNumberOnly]}
                      required={true}
                    />
                    * Author to be paid {numberToPercentage(contract.minimumGuarantee?.shareAfter)} when earnings exceed{' '}
                    {numberToCurrency(contract.minimumGuarantee?.advancePayment)} (advance payment).
                  </span>
                </div>
              )}
            </RadioGroup>
          )}
          {mode !== 'create' && !isLoading && (
            <Box alignItems="center" display={contract.dealType !== TransferContractDealType.MinimumGuarantee ? 'flex' : undefined}>
              {contract.dealType !== TransferContractDealType.MinimumGuarantee && (
                <>
                  <Typography className={classes.text}>{formattedDealType}</Typography>
                  <Typography className={classes.value}>{formattedDealAmount}</Typography>
                </>
              )}

              {contract.dealType === TransferContractDealType.MinimumGuarantee && (
                <>
                  <Typography className={classes.text}>Minimum Guarantee</Typography>
                  <div className={classes.minimumGuaranteeArea}>
                    <span className={classes.minimumGuaranteeBox}>
                      Advance Payment(Required)
                      <Typography className={classes.minimumGuaranteeValue}>{numberToCurrency(contract.minimumGuarantee?.advancePayment)}</Typography>
                      Rev. share % during Advance payment period(Required)
                      <Typography className={classes.minimumGuaranteeValue}>{numberToPercentage(contract.minimumGuarantee?.shareDuring)}</Typography>
                    </span>
                    <span className={classes.minimumGuaranteeArrow}>
                      <ForwardIcon color="primary" fontSize="large" />
                    </span>
                    <span className={classes.minimumGuaranteeBox}>
                      Rev. share % after advance payment(Required)
                      <Typography className={classes.minimumGuaranteeValue}>{numberToPercentage(contract.minimumGuarantee?.shareAfter)}</Typography>*
                      Author to be paid {numberToPercentage(contract.minimumGuarantee?.shareAfter)} when earnings exceed{' '}
                      {numberToCurrency(contract.minimumGuarantee?.advancePayment)} (advance payment).
                    </span>
                  </div>
                </>
              )}
            </Box>
          )}
          {mode !== 'create' && isLoading && <Skeleton width="300px" />}
        </Box>

        <SubmitControl
          mode={mode}
          validationMessages={[...validationMessages]}
          onSubmitClick={handleSubmitClick}
          onCancelClick={handleCancelClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          ConfirmDialogProps={{
            title: `${mode === 'create' ? 'Create' : 'Update'} this contract?`,
            text:
              mode === 'create'
                ? 'You cannot edit Target Type and Deal Type once you create the contract.'
                : 'The updated information will be used to calculate the payment amount of the target story or season.',
          }}
        />
      </Paper>
    </Box>
  );
};

export default ContractView;
